import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from './firebase';
import { createUserProfile, getUserProfile } from './babyBookService';
import { trackSignupComplete } from './analytics';

// Detect if running in Capacitor (native app)
const isNative = () => {
  return window.Capacitor !== undefined;
};

export const signUpWithEmail = async (email, password, name) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    await updateProfile(user, { displayName: name });
    
    // Create user profile with premium tracking
    await createUserProfile(user.uid, {
      email: user.email,
      displayName: name
    });
    
    await sendEmailVerification(user);

    trackSignupComplete('email');
    return { success: true, user };
  } catch (error) {
    console.error('Sign up error:', error);
    return { success: false, error: error.message };
  }
};

export const signInWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { success: true, user: userCredential.user };
  } catch (error) {
    console.error('Sign in error:', error);
    let message = 'Invalid email or password';
    if (error.code === 'auth/user-not-found') {
      message = 'No account found with this email';
    } else if (error.code === 'auth/wrong-password') {
      message = 'Incorrect password';
    } else if (error.code === 'auth/too-many-requests') {
      message = 'Too many failed attempts. Please try again later';
    }
    return { success: false, error: message };
  }
};

export const signInWithGoogle = async () => {
  try {
    let user;
    
    if (isNative()) {
      // Use redirect for native apps (Capacitor)
      await signInWithRedirect(auth, googleProvider);
      // The result will be handled by checkRedirectResult
      return { success: true, pending: true };
    } else {
      // Use popup for web
      const result = await signInWithPopup(auth, googleProvider);
      user = result.user;
    }
    
    // Check if user profile exists, create if not
    const profileResult = await getUserProfile(user.uid);
    
    if (!profileResult.success || !profileResult.userProfile) {
      await createUserProfile(user.uid, {
        email: user.email,
        displayName: user.displayName
      });
      trackSignupComplete('google');
    }

    return { success: true, user };
  } catch (error) {
    console.error('Google sign in error:', error);
    let message = 'Failed to sign in with Google';
    if (error.code === 'auth/popup-closed-by-user') {
      message = 'Sign in cancelled';
    } else if (error.code === 'auth/popup-blocked') {
      message = 'Popup blocked. Please allow popups for this site';
    }
    return { success: false, error: message };
  }
};

// Call this on app startup to check for redirect result
export const checkRedirectResult = async () => {
  try {
    const result = await getRedirectResult(auth);
    if (result && result.user) {
      const user = result.user;
      
      // Check if user profile exists, create if not
      const profileResult = await getUserProfile(user.uid);
      
      if (!profileResult.success || !profileResult.userProfile) {
        await createUserProfile(user.uid, {
          email: user.email,
          displayName: user.displayName
        });
      }
      
      return { success: true, user };
    }
    return { success: false, noRedirect: true };
  } catch (error) {
    console.error('Redirect result error:', error);
    return { success: false, error: error.message };
  }
};

export const signOutUser = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    console.error('Sign out error:', error);
    return { success: false, error: error.message };
  }
};

export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    console.error('Password reset error:', error);
    let message = 'Failed to send password reset email';
    if (error.code === 'auth/user-not-found') {
      message = 'No account found with this email';
    }
    return { success: false, error: message };
  }
};

export const resendVerificationEmail = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: 'No user signed in' };
    }
    await sendEmailVerification(user);
    return { success: true };
  } catch (error) {
    console.error('Resend verification error:', error);
    return { success: false, error: error.message };
  }
};